import { Flex, FormHelperText, FormLabel } from '@shared/components';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import { RichTextEditor, type RichTextEditorProps } from '../rich-text-editor';

export type FormRichTextEditorProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> &
    RichTextEditorProps & {
      /**
       * If true, there will be a red asterisk on the label
       */
      required?: boolean;
      /**
       * The label of the form field
       */
      label?: string;
      /**
       * The helper text of the form field
       */
      helperText?: string;
    };

export default function FormRichTextEditor<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<FormRichTextEditorProps<TFieldValues>>,
) {
  const {
    name,
    controls,
    required: isRequired,
    label,
    helperText,
    onImageUpload,
    loading: isLoading,
    placeholder,
    defaultValue,
    rows,
    ...restProps
  } = props;

  const {
    field: { ref: fieldRef, onChange, onBlur, value },
    fieldState: { error },
  } = useController({ ...restProps, name });

  const formHelperText = error?.message ?? helperText;

  return (
    <Flex
      sx={(theme) => ({
        height: 1,
        flexDirection: 'column',
        gap: theme.spacing(1),
      })}
    >
      {label ? <FormLabel required={isRequired}>{label}</FormLabel> : null}
      <RichTextEditor
        controls={controls}
        onChange={onChange}
        onBlur={onBlur}
        onImageUpload={onImageUpload}
        loading={isLoading}
        placeholder={placeholder}
        value={value ?? defaultValue}
        rows={rows}
        error={!!error}
        ref={(ref: HTMLDivElement) => {
          fieldRef({
            focus: () => ref?.scrollIntoView({ block: 'center' }),
          });
        }}
      />
      {formHelperText ? <FormHelperText error={!!error}>{formHelperText}</FormHelperText> : null}
    </Flex>
  );
}
